import React from "react";
import { useNavigate } from "react-router-dom";
import { SeverityBadge, StatusBadge } from "@/components/common/Badges";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

function Sec({ label, children }) {
    return (
        <div>
            <div className="label-eyebrow mb-2">{label}</div>
            {children}
        </div>
    );
}

/** Full explainability drawer for a single finding — used by the Findings centre
 * and by every engine page so a detection is never a dead end. */
export function FindingDetailSheet({ finding, onClose }) {
    const nav = useNavigate();
    return (
        <Sheet open={!!finding} onOpenChange={(o) => !o && onClose()}>
            <SheetContent
                data-testid="finding-detail-drawer"
                className="bg-[var(--bg-elevated)] border-l border-cyan-500/20 text-slate-200 w-full sm:max-w-2xl overflow-y-auto"
            >
                {finding && (
                    <div>
                        <SheetHeader>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <SeverityBadge severity={finding.severity} />
                                <StatusBadge status={finding.status} />
                                <span className="font-mono text-[10px] uppercase tracking-wider text-cyan-300/80">{finding.engine}</span>
                            </div>
                            <SheetTitle className="font-display text-white text-xl text-left">{finding.title}</SheetTitle>
                            <SheetDescription className="text-slate-400 text-left">
                                {finding.category} · detected {new Date(finding.created_at).toLocaleString()}
                            </SheetDescription>
                        </SheetHeader>

                        <div className="mt-6 space-y-5">
                            <Sec label="What was detected">
                                <p className="text-sm text-slate-300 whitespace-pre-wrap">{finding.description}</p>
                            </Sec>
                            {finding.business_impact && (
                                <Sec label="Business impact"><p className="text-sm text-slate-300">{finding.business_impact}</p></Sec>
                            )}
                            {finding.evidence && (
                                <Sec label={`Evidence${finding.line_number ? ` · line ${finding.line_number}` : ""}`}>
                                    <pre className="text-[11px] font-mono text-cyan-200/90 bg-black/50 border border-white/10 rounded-sm p-3 overflow-x-auto whitespace-pre-wrap">
                                        {finding.evidence}
                                    </pre>
                                </Sec>
                            )}
                            {finding.affected_objects?.length > 0 && (
                                <Sec label="Affected objects">
                                    <div className="flex flex-wrap gap-1.5">
                                        {finding.affected_objects.map((o) => (
                                            <span key={o} className="font-mono text-[10px] px-2 py-1 rounded-sm border border-white/10 text-slate-300">{o}</span>
                                        ))}
                                    </div>
                                </Sec>
                            )}
                            {finding.references?.length > 0 && (
                                <Sec label="References">
                                    <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
                                        {finding.references.map((r) => <li key={r}>{r}</li>)}
                                    </ul>
                                </Sec>
                            )}
                            <Sec label="Detection confidence">
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-[width] duration-700"
                                            style={{ width: `${Math.round((finding.confidence || 0) * 100)}%`, background: "rgb(var(--c-cyan-400))" }}
                                        />
                                    </div>
                                    <span className="font-mono text-xs text-cyan-300">{Math.round((finding.confidence || 0) * 100)}%</span>
                                </div>
                            </Sec>
                            <div className="flex gap-2 pt-4 border-t border-white/5">
                                <button
                                    data-testid="finding-open-workspace-btn"
                                    onClick={() => nav(`/workspaces/${finding.workspace_id}`)}
                                    className="btn-primary"
                                >
                                    Open workspace
                                </button>
                                <button
                                    data-testid="finding-open-recommendations-btn"
                                    onClick={() => nav("/recommendations")}
                                    className="btn-ghost"
                                >
                                    See remediation
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

/** Engine-page findings table. Rows open the explainability drawer. */
export function EngineFindingsTable({ findings, emptyHint }) {
    const [open, setOpen] = React.useState(null);
    return (
        <>
            <table className="tbl">
                <thead>
                    <tr><th>Severity</th><th>Title</th><th>Category</th><th>Affected Objects</th><th>Confidence</th><th>Status</th></tr>
                </thead>
                <tbody>
                    {findings.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-xs text-slate-500 font-mono py-8">{emptyHint}</td></tr>
                    )}
                    {findings.map((f) => (
                        <tr
                            key={f.id}
                            data-testid={`engine-finding-row-${f.id}`}
                            onClick={() => setOpen(f)}
                            className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                        >
                            <td><SeverityBadge severity={f.severity} /></td>
                            <td className="max-w-[360px]">{f.title}</td>
                            <td className="text-slate-400">{f.category}</td>
                            <td className="font-mono text-[11px] text-slate-400">{(f.affected_objects || []).join(", ")}</td>
                            <td className="font-mono text-cyan-300">{Math.round((f.confidence || 0) * 100)}%</td>
                            <td><StatusBadge status={f.status} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <FindingDetailSheet finding={open} onClose={() => setOpen(null)} />
        </>
    );
}
